import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { useAuth } from "../../utils/auth";
import { useLocation, useNavigate } from "react-router-dom";
import { authApi } from "../../api";

// Using text alternatives for icons to avoid localhost dependencies

const LoginContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  padding: 24px;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const LoginCard = styled.div`
  background: #ffffff;
  border-radius: 8px;
  width: 100%;
  max-width: 400px;
  padding: 48px 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  
  @media (max-width: 768px) {
    padding: 32px 16px;
    gap: 20px;
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  width: 100%;
`;

const Title = styled.h1`
  font-family: 'SF Pro Display', 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 590;
  font-size: 22px;
  line-height: 28px;
  color: #2f3941;
  margin: 0 0 4px 0;
  letter-spacing: 0.352px;
  width: 100%;
`;

const Subtitle = styled.p`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  margin: 0;
  letter-spacing: -0.154px;
  width: 100%;
`;

const SocialButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const SocialButton = styled.button`
  background: #ffffff;
  border: 1px solid #68737d;
  border-radius: 4px;
  height: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  cursor: pointer;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  letter-spacing: -0.154px;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #2f3941;
  }
  
  &:active {
    transform: translateY(1px);
  }
`;

const SocialIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

const Divider = styled.div`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  text-align: center;
  letter-spacing: -0.154px;
  margin: 0;
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Label = styled.label`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #2f3941;
  margin-bottom: 8px;
  letter-spacing: -0.0004px;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  background: #ffffff;
  border: 1px solid #d8dcde;
  border-radius: 4px;
  height: 40px;
  width: 100%;
  padding: 10px 12px;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #2f3941;
  letter-spacing: -0.0004px;
  
  &::placeholder {
    color: #68737d;
  }
  
  &:focus {
    outline: none;
    border-color: #8d4f1b;
  }
  
  &.error {
    border-color: #cc3340;
  }
`;

const PasswordInput = styled(Input)`
  padding-right: 40px;
`;

const EyeIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  user-select: none;
`;

const ForgotPasswordLink = styled.div`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #8d4f1b;
  text-align: right;
  letter-spacing: -0.154px;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SignInButton = styled.button`
  background: #8d4f1b;
  color: #ffffff;
  border: none;
  border-radius: 4px;
  height: 40px;
  width: 100%;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.154px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #703815;
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:disabled {
    background: #d8dcde;
    cursor: not-allowed;
  }
`;

const BottomText = styled.p`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #68737d;
  text-align: center;
  letter-spacing: -0.154px;
  margin: 0;
`;

const RegisterLink = styled.span`
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: bold;
  color: #8d4f1b;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  color: #cc3340;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const SuccessMessage = styled.div`
  color: #038153;
  font-size: 12px;
  margin-top: 4px;
  font-family: 'SF Pro', -apple-system, BlinkMacSystemFont, sans-serif;
  background-color: #f0f9f5;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px solid #038153;
`;

const Login: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState<{ 
    email?: string; 
    password?: string; 
    general?: string; 
  }>({});
  const [successMessage, setSuccessMessage] = useState("");
  const { login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const from = location.state?.from?.pathname || "/";

  // Handle success message from OTP verification or other sources
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      // Pre-fill email if provided
      if (location.state.email) {
        setFormData(prev => ({ ...prev, email: location.state.email }));
      }
    }
  }, [location.state]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
    // Clear success message when user starts typing
    if (successMessage) {
      setSuccessMessage("");
    }
  };

  const validateForm = () => {
    const newErrors: typeof errors = {};
    
    if (!formData.email) {
      newErrors.email = "Please enter your email address";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }
    
    if (!formData.password) {
      newErrors.password = "Please enter your password";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    setErrors({});
    
    try {
      console.log('🚀 Login Component: Starting login process...');
      console.log('📧 Email:', formData.email);
      
      // Call the login method from auth context which handles the API call
      console.log('🔄 Login Component: Calling auth context login...');
      const authResponse = await login(formData.email, formData.password);
      
      console.log('✅ Login Component: Auth response received:', authResponse);
      console.log('👤 Login Component: User data:', authResponse.user);
      console.log('🏷️ Login Component: User role:', authResponse.user?.role);
      
      // Small delay to ensure auth context updates
      await new Promise(resolve => setTimeout(resolve, 100));
      console.log('⏱️ Login Component: Auth update delay completed');
      
      // Navigate based on user role
      const { user } = authResponse;
      console.log('🧭 Login Component: Determining navigation for role:', user.role);
      
      switch (user.role) {
        case 'physician':
          console.log('🩺 Navigating to /physician');
          navigate("/physician");
          break;
        case 'specialist':
          console.log('🔬 Navigating to /reviewer');
          navigate("/reviewer");
          break;
        case 'admin':
        case 'super-admin':
          console.log('👨‍💼 Navigating to /admin');
          navigate("/admin");
          break;
        default:
          console.log('🏠 Navigating to home (default)');
          navigate("/");
      }
      
      console.log('✅ Login Component: Navigation command sent');
      
      // Double-check localStorage after navigation
      setTimeout(() => {
        console.log('🔍 Post-login localStorage check:');
        console.log('- Token:', localStorage.getItem('authToken') ? 'Present' : 'Missing');
        console.log('- User:', localStorage.getItem('user') ? 'Present' : 'Missing');
      }, 500);
      
    } catch (error: any) {
      console.error('❌ Login Component: Login failed:', error.message);
      console.error('❌ Full error object:', error);
      
      // Check if the error indicates an unverified account
      if (error.message.includes('verify') || error.message.includes('confirmed') || error.message.includes('pending')) {
        // Redirect to OTP verification page
        navigate("/verify-otp", { 
          state: { 
            email: formData.email,
            message: "Please verify your email address to complete your registration."
          } 
        });
      } else {
        setErrors({ general: error.message });
      }
    }
    setLoading(false);
  };

  // Disabled social sign-in handlers
  // const handleGoogleLogin = () => {
  //   window.location.href = authApi.getGoogleLoginUrl();
  // };

  // const handleAppleLogin = () => {
  //   // Apple sign-in logic would go here
  //   console.log("Apple sign-in clicked");
  // };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  const handleRegisterClick = () => {
    navigate("/signup");
  };

  return (
    <LoginContainer>
      <LoginCard>
        <TitleSection>
          <Title>Welcome Back!</Title>
          <Subtitle>Please Sign In to Your Account</Subtitle>
        </TitleSection>
        
        {/* Disabled social sign-in buttons
        <SocialButtonsContainer>
          <SocialButton type="button" onClick={handleGoogleLogin}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Sign In with Google
          </SocialButton>
          <SocialButton type="button" onClick={handleAppleLogin}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#000"/>
            </svg>
            Sign In with Apple
          </SocialButton>
        </SocialButtonsContainer>
        
        <Divider>or continue with email</Divider>
        */}
        
        <FormContainer onSubmit={onSubmit}>
          <InputGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              id="email"
              type="email"
              placeholder="Enter your email address"
              value={formData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className={errors.email ? "error" : ""}
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </InputGroup>
          
          <InputGroup>
            <Label htmlFor="password">Password</Label>
            <InputWrapper>
              <PasswordInput
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                className={errors.password ? "error" : ""}
              />
              <EyeIcon onClick={togglePasswordVisibility}>
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </EyeIcon>
            </InputWrapper>
            {errors.password && <ErrorMessage>{errors.password}</ErrorMessage>}
          </InputGroup>
          
          <ForgotPasswordLink onClick={handleForgotPassword}>
            Forgot Password?
          </ForgotPasswordLink>
          
          {successMessage && <SuccessMessage>{successMessage}</SuccessMessage>}
          {errors.general && <ErrorMessage>{errors.general}</ErrorMessage>}
          
          <SignInButton 
            type="submit" 
            disabled={loading}
          >
            {loading ? "Signing In..." : "Sign In"}
          </SignInButton>
        </FormContainer>
        
        <BottomText>
          Don't have an account?{" "}
          <RegisterLink onClick={handleRegisterClick}>
            Sign up for free
          </RegisterLink>
        </BottomText>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;